import { Check, ChevronDown, Search, X } from 'lucide-react'
import * as React from 'react'
import { createPortal } from 'react-dom'

import { Input } from '@/components/ui/input'
import { cn, fuzzyIncludes } from '@/lib/utils'

import type { ComboboxOption } from './combobox'

const MAX_PANEL_HEIGHT = 340
const MIN_PANEL_HEIGHT = 180
const GAP = 6

interface MultiComboboxProps {
  value: string[]
  onChange: (value: string[]) => void
  onSearch?: (query: string) => void
  options: ComboboxOption[]
  placeholder?: string
  searchPlaceholder?: string
  loading?: boolean
  emptyText?: string
  invalid?: boolean
  disabled?: boolean
  className?: string
}

export function MultiCombobox({
  value,
  onChange,
  onSearch,
  options,
  placeholder,
  searchPlaceholder,
  loading,
  emptyText = '—',
  invalid,
  disabled,
  className
}: MultiComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const [coords, setCoords] = React.useState<{
    top?: number
    bottom?: number
    left: number
    width: number
    maxHeight: number
  }>({ top: 0, left: 0, width: 0, maxHeight: MAX_PANEL_HEIGHT })
  const ref = React.useRef<HTMLDivElement>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)

  const reposition = React.useCallback(() => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const spaceBelow = window.innerHeight - r.bottom - GAP - 8
    const spaceAbove = r.top - GAP - 8
    const openUp = spaceBelow < MIN_PANEL_HEIGHT && spaceAbove > spaceBelow
    const available = openUp ? spaceAbove : spaceBelow
    const maxHeight = Math.max(
      MIN_PANEL_HEIGHT,
      Math.min(MAX_PANEL_HEIGHT, available)
    )
    const width = Math.max(r.width, 220)
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
    if (openUp) {
      setCoords({
        bottom: window.innerHeight - r.top + GAP,
        left,
        width,
        maxHeight
      })
    } else {
      setCoords({ top: r.bottom + GAP, left, width, maxHeight })
    }
  }, [])

  React.useLayoutEffect(() => {
    if (!open) return
    reposition()
    searchRef.current?.focus({ preventScroll: true })
  }, [open, reposition])

  React.useEffect(() => {
    if (!open) return
    const onScroll = () => reposition()
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        !ref.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    document.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open, reposition])

  const labelFor = (val: string) =>
    options.find((o) => o.value === val)?.label ?? val

  const toggle = (val: string) =>
    onChange(
      value.includes(val) ? value.filter((v) => v !== val) : [...value, val]
    )

  const filtered = onSearch
    ? options
    : options.filter((o) => fuzzyIncludes(o.label, query))

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex min-h-10 w-full items-center gap-1.5 rounded-2xl border bg-white px-3 py-1.5 text-left text-sm transition-colors hover:border-ink/30 focus:border-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
          invalid ? 'border-destructive' : 'border-ink/[0.16]'
        )}
      >
        <span className="flex max-h-[100px] flex-1 flex-wrap items-center gap-1.5 overflow-y-auto">
          {value.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            value.map((val) => (
              <span
                key={val}
                className="inline-flex items-center gap-1 rounded-full bg-ink/[0.06] py-0.5 pl-2.5 pr-1 text-xs text-ink"
              >
                <span className="max-w-[14rem] truncate">{labelFor(val)}</span>
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggle(val)
                  }}
                  className="flex h-4 w-4 items-center justify-center rounded-full text-ink/50 hover:text-ink"
                >
                  <X className="h-3 w-3" />
                </span>
              </span>
            ))
          )}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            data-floating-panel=""
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: coords.top,
              bottom: coords.bottom,
              left: coords.left,
              width: coords.width,
              maxHeight: coords.maxHeight
            }}
            className="pointer-events-auto z-[1300] flex flex-col overflow-hidden rounded-2xl border border-ink/[0.08] bg-white shadow-[0_16px_40px_rgba(4,33,37,0.16)]"
          >
            <div className="relative shrink-0 border-b border-ink/[0.06] p-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchRef}
                value={query}
                placeholder={searchPlaceholder ?? placeholder}
                onChange={(e) => {
                  setQuery(e.target.value)
                  onSearch?.(e.target.value)
                }}
                className="h-9 border-0 pl-9 shadow-none focus-visible:ring-0"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-1">
              {loading ? (
                <div className="px-3 py-3 text-sm text-muted-foreground">…</div>
              ) : filtered.length === 0 ? (
                <div className="px-3 py-3 text-sm text-muted-foreground">
                  {emptyText}
                </div>
              ) : (
                filtered.map((o) => {
                  const selected = value.includes(o.value)
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => toggle(o.value)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-xl border-0 bg-transparent px-3 py-2 text-left text-sm transition-colors hover:bg-ink/[0.04]',
                        selected ? 'font-semibold text-primary' : 'text-ink'
                      )}
                    >
                      <span className="truncate">{o.label}</span>
                      {selected && <Check className="h-4 w-4 shrink-0" />}
                    </button>
                  )
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
